import {EnrichedAstNode} from '../../../types';
import {findClosestParentWithFontSize} from '../../../AstSearchers/FindAst';
import {DEFAULT_EM_VALUE, PIXELS_TO_REM_RATIO} from '../../constants';
import {extractUnitAndValue} from '../extractUnitAndValue';

/**
 * @description Converting `font-size` to pixels is kinda tricky because it's relative to parent font size making it
 * recursively resolved. Since the parent can have different units, we can just opt for converting to pixels which will
 * be an absolute value rather than relative. This is obviously recursive and not ideal.
 */
export const convertFontSizeOfNodeToPixels = (
    view: EnrichedAstNode,
    node: EnrichedAstNode | null,
    parentFontSizePx?: number
): number => {
    if (!node) {
        return DEFAULT_EM_VALUE;
    }

    const {styles} = node;
    const {fontSize} = styles;

    const {value: fontSizeValue, unit: fontSizeUnit} = extractUnitAndValue(fontSize + '', DEFAULT_EM_VALUE);

    if (Number.isNaN(fontSizeValue)) {
        // In this case something went wrong with parsing the font-size. We can just treat it as 16px
        return DEFAULT_EM_VALUE;
    }

    // there are values like inherit, initial, revert, unset, math, smaller, large, xx-large, etc...
    // we're just not going to worry about those for the time being
    if (typeof fontSizeValue === 'string') {
        return DEFAULT_EM_VALUE;
    }

    switch (fontSizeUnit) {
        case 'px':
            return fontSizeValue;
        case 'em':
            return (
                fontSizeValue *
                (parentFontSizePx ??
                    convertFontSizeOfNodeToPixels(view, findClosestParentWithFontSize(view, node.internalId)))
            );
        case 'rem':
            return fontSizeValue * PIXELS_TO_REM_RATIO;
        case '%':
            return (
                (fontSizeValue / 100) *
                (parentFontSizePx ??
                    convertFontSizeOfNodeToPixels(view, findClosestParentWithFontSize(view, node.internalId)))
            );
        default:
            return fontSizeValue;
    }
};
